// @flow

// Types
import type {
  SamplingPointOperation,
} from '../../types'

import type {
  Point as SamplingPoint,
} from 'fc-model-sampling-points/types'

// Libs
import _find from 'lodash/find'
import _get from 'lodash/get'

// Constants
import {
  URL as SAMPLING_POINT_URL,
  PROP_SAMPLING_POINT_ID,
  PROP_LATITUDE,
  PROP_LONGITUDE,
  PROP_MANAGEMENT_ZONE,
  PROP_GRID_ID,
  PROP_JOB,
} from 'fc-model-sampling-points/src/constants'

// Operator
import operator from '../operator'

// Controller
import controller from 'fc-ctrl-sampling-points'

// Effect Managers
import { call, fork, put, select, take, race } from 'redux-saga/effects'

// Selectors
import operation$ from 'hfty-sampling-point/src/selectors/editPoint'
import samplingPoints$ from 'hfty-soil-jobs/src/selectors/samplingPoints'
import { getToken } from 'fc-auth'

// Utils
import url from 'hfty-utils/url'

// Actions
import { EDIT_POINT, SAVE_GRID_ID } from 'hfty-sampling-point/src/constants'

// Exports
export default function *watchOperations (): Generator<*, void, *> {
  while (true) {
    const { payload: id } = yield take([EDIT_POINT])
    const samplePoints = yield select(samplingPoints$)
    const samplePoint = _find(samplePoints, { id })
    const operation = operator.create({
      model: {
        [PROP_SAMPLING_POINT_ID]: samplePoint[PROP_SAMPLING_POINT_ID],
        [PROP_LATITUDE]: samplePoint[PROP_LATITUDE],
        [PROP_LONGITUDE]: samplePoint[PROP_LONGITUDE],
        [PROP_MANAGEMENT_ZONE]: samplePoint[PROP_MANAGEMENT_ZONE],
        [PROP_GRID_ID]: samplePoint[PROP_GRID_ID],
        [PROP_JOB]: samplePoint[PROP_JOB],
      },
    })
    yield put(operator.actionCreators.edit(operation))
    yield fork(handleOperations)
  }
}

export function *handleOperations (): Generator<*, void, *> {
  let done = false
  while (!done) {
    const { submit } = yield race({
      submit: take([SAVE_GRID_ID]),
      cancel: take(operator.actionTypes.cancelOperation()),
    })
    if (submit) {
      done = yield call(handleSubmitOperation)
    } else {
      done = true
    }
  }
}

export function *handleSubmitOperation (): Generator<*, SamplingPointOperation, *> {
  let operation = yield select(operation$)
  const model: SamplingPoint = operation.model
  const pointId = model[PROP_SAMPLING_POINT_ID]
  const token = yield call(getToken)

  const response = yield call(fetch, url({
    // path: `/soilsampling/point/`,
    path: `/${SAMPLING_POINT_URL}`,
    id: pointId,
  }), {
    method: 'PUT',
    body: JSON.stringify(model),
    headers: {
      Authorization: `Token ${token}`,
      'Content-Type': 'application/json',
    },
  })

  const error = response.status < 200 || response.status >= 300

  if (error) {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: false,
      errors: _get(response, 'statusText', 'Error updating Bag ID'),
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    return nextOperation
  } else {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: true,
    }
    const action = operator.actionCreators.cancelOperation(nextOperation)
    yield put(action)

    yield put(controller.actionCreators.receive({
      id: pointId,
      data: {
        [pointId]: {
          ...model,
        },
      },
    }))

    yield put(action)

    return nextOperation
  }
}
